import { useI18n } from '@/hooks/useI18n';
import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Empty } from '@/components/primitives/Empty';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { useAccountReels } from '@/hooks/queries/useAccountReels';
import type { ReelResponse } from '@/types/api';
import { ReelRow } from './ReelRow';
import { ReelLightbox } from './ReelLightbox';

const PAGE_SIZE = 20;

interface ReelsRegistryProps {
  accountId: string;
}

export function ReelsRegistry({ accountId }: ReelsRegistryProps) {
  const { t } = useI18n();
  const [page, setPage] = useState(0);
  const [all, setAll] = useState<ReelResponse[]>([]);
  const [openReel, setOpenReel] = useState<ReelResponse | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isFetching, error, refetch } = useAccountReels(accountId, {
    page,
    size: PAGE_SIZE,
    sort: 'publishedAt,desc',
  });

  useEffect(() => {
    if (!data) return;
    if (data.number === 0) setAll(data.content);
    else
      setAll((prev) => {
        const ids = new Set(prev.map((r) => r.reelId));
        return [...prev, ...data.content.filter((r) => !ids.has(r.reelId))];
      });
  }, [data]);

  useEffect(() => {
    setPage(0);
    setAll([]);
  }, [accountId]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !data || data.last) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetching) setPage((p) => p + 1);
      },
      { rootMargin: '400px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [data, isFetching]);

  if (error) return <ErrorInline error={error} onRetry={() => refetch()} />;

  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-text-1">Reels</h2>
          <p className="text-[12px] text-text-3">
            {data
              ? t(
                  `${all.length} / ${data.totalElements} · triés par date`,
                  `${all.length} / ${data.totalElements} · sorted by date`,
                )
              : t('triés par date', 'sorted by date')}
          </p>
        </div>
      </header>

      {isLoading && all.length === 0 ? (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[120px] w-full rounded-[14px]" />
          ))}
        </div>
      ) : all.length === 0 ? (
        <Empty
          title={t('Aucun reel scrapé', 'No scraped reels')}
          description={t(
            'Lancez un scrape manuel pour collecter les reels de ce compte.',
            'Run a manual scrape to collect this account’s reels.',
          )}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {all.map((r) => (
              <ReelRow key={r.reelId} reel={r} onClick={() => setOpenReel(r)} />
            ))}
          </div>
          <div
            ref={sentinelRef}
            className="flex h-10 items-center justify-center font-mono text-[11px] text-text-4"
          >
            {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          </div>
        </>
      )}

      <ReelLightbox
        reel={openReel}
        allReels={all}
        open={!!openReel}
        onOpenChange={(o) => !o && setOpenReel(null)}
      />
    </section>
  );
}
